import React, { useEffect, useRef, useMemo } from "react";
import { Box, Typography, Button } from "@mui/material";
// import ResponsiveModal from "@/modules/common/components/ui-kit/modals/responsive-modal";
import CommonModal from "@/modules/common/components/ui-kit/modals/common-modal";
import WithResponsiveRendering from "@/modules/common/components/wrappers/with-responsive-rendering";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";
import useCreateTrack from "../hooks/use-create-track.hook";
import CreateTrackRow from "./create-track-row";
import { Plus } from "lucide-react";

interface CreateTrackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CreateTrackModal: React.FC<CreateTrackModalProps> = ({
  isOpen,
  onClose,
}) => {
  const {
    createdTracks,
    handleAddTrack,
    handleDeleteTrack,
    handleTrackChange,
    resetTracks,
    isSubmitting,
    handleSubmit,
    isValid,
    canAddNewTrack,
    trackErrors,
  } = useCreateTrack({ onClose });

  // Scroll to bottom when a new track is added
  const bottomRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (createdTracks.length > 0) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [createdTracks.length]);

  const handleClose = () => {
    resetTracks();
    onClose();
  };

  const ResponsiveModal = useMemo(() => WithResponsiveRendering({
    MobileComponent: CommonModal,
    DesktopComponent: CommonModal,
  }), []);

  return (
    <ResponsiveModal
      open={isOpen}
      onClose={handleClose}
      title="New developer track"
      confirmButtonText="Create"
      cancelButtonText="Cancel"
      onConfirm={handleSubmit}
      isLoading={isSubmitting}
      isConfirmDisabled={!isValid}
      sx={{
        width: "600px",
        maxWidth: "95vw",
      }}
    >
      <Box
        sx={{
          padding: "24px",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: nawyTheme.nawy.spacing.md,
          maxHeight: "70vh",
          overflowY: "auto",
        }}
      >
        <Typography
          sx={{
            fontSize: "14px",
            color: "#636363",
          }}
        >
          Add one or more developer tracks.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {createdTracks.map((track, index) => (
            <CreateTrackRow
              key={track.id}
              track={track}
              index={index}
              onDelete={handleDeleteTrack}
              onChange={handleTrackChange}
              canDelete={createdTracks.length > 1}
              errors={trackErrors[track.id]}
            />
          ))}
          <div ref={bottomRef} />
        </Box>

        <Button
          startIcon={<Plus size={18} />}
          onClick={handleAddTrack}
          disabled={!canAddNewTrack}
          sx={{
            width: "fit-content",
            textTransform: "none",
            color: nawyTheme.nawy.colors.semantic.layer.accent,
            ...nawyTheme.nawy.typography.english.variants.body.md.semibold,
            "&:hover": {
              backgroundColor: "transparent",
              textDecoration: "underline",
            },
            padding: 0,
            marginTop: 1,
          }}
        >
          Add another track
        </Button>
      </Box>
    </ResponsiveModal>
  );
};

export default CreateTrackModal;
